import { useNavigate } from "react-router-dom";
import { MapPin, Users, ArrowUpRight, Buildings, ShieldCheck, Clock, Target, ShieldWarning, Broadcast, Stack, EyeSlash, CurrencyEur } from "@phosphor-icons/react";
import { postingAge, remoteLabel, companyClassMeta, salaryLabel, tjmLabel } from "@/lib/format";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const RING = { selected: "rgba(138,138,147,0.5)", contacted: "rgba(249,115,22,0.55)", replied: "rgba(34,197,94,0.55)" };

const CATEGORY = {
  direct: { label: "Correspondance directe", color: "#22C55E" },
  equivalent: { label: "Métier équivalent", color: "#00E5FF" },
  related: { label: "Métier proche pertinent", color: "#7C5CFF" },
};

const HIDE_REASONS = [
  { v: "not_relevant", label: "Non pertinent" },
  { v: "wrong_role", label: "Mauvais métier" },
  { v: "wrong_location", label: "Mauvaise localisation" },
  { v: "already_handled", label: "Déjà traité" },
  { v: "other", label: "Autre / sans motif" },
];

export default function OpportunityCard({ item, index = 0, onHide, onHideCompany }) {
  const navigate = useNavigate();
  const { opportunity, company, score, matched_skills, distance_km, contacts_count,
    verified_emails, opportunity_score, match_reasons, match_category, match_reason,
    posted_date, positioning, company_class, tjm } = item;

  const skills = opportunity.skills || [];
  const shown = skills.slice(0, 5);
  const extra = skills.length - shown.length;
  const age = postingAge(posted_date || opportunity.posted_at);
  const remote = remoteLabel(opportunity.remote_status);
  const tjmTxt = tjmLabel(tjm);
  const sal = tjmTxt ? { label: tjmTxt, kind: "tjm" } : salaryLabel(opportunity);
  const matchColor = score >= 80 ? "#00E5FF" : score >= 55 ? "#4F46E5" : "#8A8A93";
  const ringColor = positioning ? (RING[positioning.state] || RING.selected) : null;
  const cat = CATEGORY[match_category];

  const open = () => navigate(`/app/opportunity/${opportunity.id}`, {
    state: { score, opportunity_score, distance_km, match_reasons, contacts_count, verified_emails },
  });

  return (
    <div
      className="surface rounded-lg p-5 hover-lift cursor-pointer group fade-up flex flex-col"
      style={{ animationDelay: `${index * 40}ms`, ...(ringColor ? { boxShadow: `0 0 0 1px ${ringColor}` } : {}) }}
      data-testid={`opportunity-card-${opportunity.id}`}
      onClick={open}
    >
      {positioning && (
        <div className="flex items-center gap-1.5 mb-3 -mt-1" data-testid={`opportunity-positioned-${opportunity.id}`}>
          <CommercialStatusBadge positioning={positioning} opportunityId={opportunity.id} companyName={company.name} />
        </div>
      )}
      {/* header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: `${company.logo_color}1a`, border: `1px solid ${company.logo_color}40` }}>
            <Buildings size={18} weight="duotone" style={{ color: company.logo_color }} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold text-fg leading-tight truncate">{opportunity.role_title}</h3>
            <div className="text-sm text-muted truncate">{company.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          <span className="font-mono text-xs font-bold px-2 py-1 rounded-full border"
            style={{ color: matchColor, borderColor: matchColor + "55", background: matchColor + "14" }}
            data-testid="match-badge">
            {score}% match
          </span>
          {onHide && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`hide-opportunity-${opportunity.id}`} title="Masquer ce signal"
                  className="w-7 h-7 flex items-center justify-center rounded-md text-muted hover:text-fg hover:bg-surface-2 border border-transparent hover:border-border transition-colors">
                  <EyeSlash size={15} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">Masquer ce signal (pour vous)</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {HIDE_REASONS.map((r) => (
                  <DropdownMenuItem key={r.v} data-testid={`hide-reason-${r.v}`}
                    onClick={() => onHide(item, r.v)} className="text-xs cursor-pointer">{r.label}</DropdownMenuItem>
                ))}
                {onHideCompany && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs">Entreprise</DropdownMenuLabel>
                    <DropdownMenuItem data-testid={`hide-company-${company.id}`}
                      onClick={() => onHideCompany(item)} className="text-xs cursor-pointer text-rose-400">
                      Masquer {company.name} (toute l'organisation)
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* location + meta */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs text-muted font-mono">
        <span className="flex items-center gap-1"><MapPin size={12} /> {opportunity.city || "—"}{distance_km != null ? ` · ${distance_km} km` : ""}</span>
        <span>{opportunity.seniority}</span>
        {opportunity.canonical_family_name && (
          <span className="flex items-center gap-1 text-fg/70 border border-border rounded px-1.5 py-0.5" data-testid="role-family-tag" title={opportunity.canonical_role_name || ""}>
            <Stack size={11} /> {opportunity.canonical_family_name}
          </span>
        )}
        {cat && (
          <span className="flex items-center gap-1 border rounded px-1.5 py-0.5" data-testid="match-category-tag"
            style={{ color: cat.color, borderColor: cat.color + "66" }}
            title={match_reason ? `${cat.label} — ${match_reason}` : cat.label}>
            <Target size={11} weight="fill" /> {cat.label}
          </span>
        )}
        {remote && <span>{remote}</span>}
        {sal && (
          <span className="flex items-center gap-1 font-semibold rounded px-1.5 py-0.5 border"
            style={{ color: "#34D399", borderColor: "#34D39955", background: "#34D39914" }}
            data-testid="tjm-badge"
            title={sal.kind === "tjm" ? "Tarif journalier moyen (TJM)" : "Rémunération indiquée dans l'annonce"}>
            <CurrencyEur size={11} weight="fill" /> {sal.label}
          </span>
        )}
        {age && <span className="flex items-center gap-1"><Clock size={12} /> {age}</span>}
        {(() => {
          const ccm = companyClassMeta(company_class || (opportunity.client_type === "intermediary" ? "ESN_CONSULTING" : "FINAL_CLIENT"));
          return (
            <span className="flex items-center gap-1 rounded px-1.5 py-0.5 border font-semibold" data-testid="company-class-tag"
              style={{ color: ccm.color, borderColor: ccm.color + "66", background: ccm.color + "14" }}
              title={`Classification : ${ccm.label}`}>
              {ccm.key === "FINAL_CLIENT" ? <Buildings size={11} weight="fill" /> : ccm.key === "UNKNOWN" ? <Target size={11} /> : <ShieldWarning size={11} weight="fill" />} {ccm.label}
            </span>
          );
        })()}
        {opportunity.source_count > 1 && (
          <span className="flex items-center gap-1 text-accent border border-accent/30 rounded px-1.5 py-0.5" data-testid="multi-source-tag" title="Ce besoin est détecté sur plusieurs canaux — signal de demande soutenue">
            <Broadcast size={11} weight="fill" /> {opportunity.source_count} sources
          </span>
        )}
        {opportunity.direct_company_source && (
          <span className="flex items-center gap-1 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5" data-testid="direct-source-tag" title="Offre publiée directement sur le site carrière de l'employeur">
            <ShieldCheck size={11} weight="fill" /> Source directe
          </span>
        )}
      </div>

      {/* skills */}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {shown.map((s) => {
          const m = matched_skills.includes(s);
          return (
            <span key={s} className={`text-xs font-mono px-2 py-0.5 rounded ${m ? "bg-accent/15 text-accent border border-accent/30" : "bg-surface-2 text-muted border border-border"}`}>{s}</span>
          );
        })}
        {extra > 0 && <span className="text-xs font-mono px-2 py-0.5 rounded bg-surface-2 text-muted border border-border">+{extra} autres</span>}
      </div>

      {/* footer */}
      <div className="mt-auto pt-4 flex items-center justify-between gap-3 border-t border-border mt-4">
        <div className="flex items-center gap-3 text-xs text-muted font-mono">
          <span className="flex items-center gap-1"><Target size={12} className="text-accent" /> Opp. {opportunity_score}/100</span>
          <span className="flex items-center gap-1">
            <Users size={12} /> {contacts_count} contact{contacts_count > 1 ? "s" : ""}
            {verified_emails ? <span className="text-accent flex items-center gap-0.5 ml-1"><ShieldCheck size={11} weight="fill" />{verified_emails}</span> : null}
          </span>
        </div>
        <span className="flex items-center gap-1 text-sm text-fg group-hover:text-accent transition-colors">
          Voir <ArrowUpRight size={14} />
        </span>
      </div>
    </div>
  );
}
